import { ContentPlayback } from './ContentPlayback';
import { Logger, Sound } from '@/classes';
import { keyCodes } from '@/constants';
import { PopupContext } from '@/contexts/PopupContext';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { useRouter } from '@/hooks/useRouter';
import { Content } from '@/types';
import { focusNeighborInView } from '@/utils';
import { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseContentCardProps {
  content: Content;
  sourcePage: string;
  weight: number;
  focusKey?: string;
  containerId?: number;
  topicId?: number;
  onFocused?: (layout: FocusableComponentLayout) => void;
  onExposed?: () => void;
}

export const useContentCard = ({
  content,
  sourcePage,
  weight,
  focusKey,
  containerId,
  topicId,
  onFocused,
  onExposed,
}: UseContentCardProps) => {
  const router = useRouter();
  const [searchParams] = useSearchParams();
  const popupContext = useContext(PopupContext);
  const { ref, focused, focusSelf } = useExtendFocusable({
    ...(focusKey ? { focusKey } : {}),
    onFocus: onFocused,
    onArrowPress: (direction) => focusNeighborInView(direction),
  });

  const progress = useMemo(() => ContentPlayback.progressOf(content.id), [content.id]);

  const handleClick = useCallback(() => {
    Sound.press();
    if (content.type !== 'VIDEO') {
      return;
    }
    if (ContentPlayback.isLocked(content)) {
      popupContext?.showSubscribePopup(sourcePage, focusKey);
      return;
    }
    Logger.clickRaw(content.id, sourcePage, weight, containerId, topicId);
    router.push(ContentPlayback.toPlayerPath({ content, sourcePage, containerId }), { focusKey });
  }, [containerId, content, focusKey, popupContext, router, sourcePage, topicId, weight]);

  useEffect(() => {
    if (focusKey === searchParams.get('f')) {
      focusSelf();
    }
  }, [focusKey, focusSelf, searchParams]);

  useFocusElement({ ref, focused, onClick: handleClick, keyCode: keyCodes.ENTER });

  const alreadyExposedRef = useRef(false);
  const handleExposed = useCallback(() => {
    if (!alreadyExposedRef.current) {
      Logger.exposedRaw(content.id, sourcePage, weight, containerId, topicId);
      alreadyExposedRef.current = true;
    }
    onExposed?.();
  }, [containerId, content.id, onExposed, sourcePage, weight, topicId]);

  return { ref, progress, handleClick, handleExposed };
};
